import { useMemo } from 'react'
import {
    ChangeLogFieldsFragment,
    ClientPreviewFieldsFragment,
    CustomerSegmentPreviewFieldsFragment,
    useQueryClientsQuery,
    useQueryCustomerSegmentsQuery,
    useQueryUsersQuery,
    UserFieldsFragment,
} from '~graphql/generated/graphql'
import { inStringArrFilter } from '~utils/query-helpers'

/**
 * Field keys that correspond to respective entity IDs in change log values
 */
export const USER_FIELD_KEYS: string[] = ['user', 'assignee']
export const CLIENT_FIELD_KEYS: string[] = ['client', 'owner']
export const CUSTOMER_SEGMENT_FIELD_KEYS: string[] = ['customer_segment']

export type UserEntityMap = Map<string, UserFieldsFragment>
export type ClientEntityMap = Map<string, ClientPreviewFieldsFragment>
export type CustomerSegmentEntityMap = Map<
    string,
    CustomerSegmentPreviewFieldsFragment
>

/**
 * All entity lookup maps for change log value resolution
 */
export interface ChangeLogEntities {
    users: UserEntityMap
    clients: ClientEntityMap
    customerSegments: CustomerSegmentEntityMap
    loading: boolean
}

type MapWithLoading<T> = {
    map: Map<string, T>
    loading: boolean
}

/**
 * Builds a lookup map from an array of entities with an `id` property.
 * This is a helper to reduce boilerplate when creating entity maps from query results.
 */
function buildEntityMap<T extends { id: string }>(
    entities: T[] | null | undefined,
): Map<string, T> {
    const map = new Map<string, T>()
    for (const entity of entities ?? []) {
        map.set(entity.id, entity)
    }
    return map
}

/**
 * Extracts all entity IDs of a given type from change log old_values and new_values
 */
function extractEntityIds(
    changeLogs: ChangeLogFieldsFragment[],
    fieldKeys: string[],
): string[] {
    const ids = new Set<string>()

    for (const changeLog of changeLogs) {
        const oldValues = changeLog.old_values ?? {}
        const newValues = changeLog.new_values ?? {}

        for (const fieldKey of fieldKeys) {
            const oldValue = oldValues[fieldKey]
            const newValue = newValues[fieldKey]

            if (typeof oldValue === 'string' && oldValue) {
                ids.add(oldValue)
            }
            if (typeof newValue === 'string' && newValue) {
                ids.add(newValue)
            }
        }
    }

    return Array.from(ids)
}

/**
 * Hook to batch-fetch all entities referenced in change logs
 *
 * Instead of making individual queries for each entity in FormattedValue,
 * this hook collects all referenced entity IDs and makes batch queries,
 * returning lookup maps for efficient resolution.
 *
 * @param changeLogs - Array of change log entries to extract entities from
 * @returns ChangeLogEntities with lookup maps for users and clients
 */
export function useChangeLogEntities(
    changeLogs: ChangeLogFieldsFragment[],
): ChangeLogEntities {
    const { map: users, loading: usersLoading } =
        useGetUserChangeLogMap(changeLogs)
    const { map: clients, loading: clientsLoading } =
        useGetClientChangeLogMap(changeLogs)
    const { map: customerSegments, loading: customerSegmentsLoading } =
        useGetCustomerSegmentChangeLogMap(changeLogs)

    return {
        users,
        clients,
        customerSegments,
        loading: usersLoading || clientsLoading || customerSegmentsLoading,
    }
}

function useGetUserChangeLogMap(
    changeLogs: ChangeLogFieldsFragment[],
): MapWithLoading<UserFieldsFragment> {
    const userIds = useMemo(
        () => extractEntityIds(changeLogs, USER_FIELD_KEYS),
        [changeLogs],
    )
    const { data: usersData, loading: usersLoading } = useQueryUsersQuery({
        variables: {
            where: [inStringArrFilter('id', userIds)],
        },
        skip: userIds.length === 0,
    })

    return {
        map: buildEntityMap(usersData?.queryUsers),
        loading: usersLoading,
    }
}

function useGetClientChangeLogMap(
    changeLogs: ChangeLogFieldsFragment[],
): MapWithLoading<ClientPreviewFieldsFragment> {
    const clientIds = useMemo(
        () => extractEntityIds(changeLogs, CLIENT_FIELD_KEYS),
        [changeLogs],
    )
    const { data: clientsData, loading: clientsLoading } = useQueryClientsQuery(
        {
            variables: {
                where: [inStringArrFilter('id', clientIds)],
            },
        },
    )

    return {
        map: buildEntityMap(clientsData?.queryClients),
        loading: clientsLoading,
    }
}

function useGetCustomerSegmentChangeLogMap(
    changeLogs: ChangeLogFieldsFragment[],
): MapWithLoading<CustomerSegmentPreviewFieldsFragment> {
    const customerSegmentIds = useMemo(
        () => extractEntityIds(changeLogs, CUSTOMER_SEGMENT_FIELD_KEYS),
        [changeLogs],
    )
    const { data: customerSegmentsData, loading: customerSegmentsLoading } =
        useQueryCustomerSegmentsQuery({
            variables: {
                where: [inStringArrFilter('id', customerSegmentIds)],
            },
            skip: customerSegmentIds.length === 0,
        })

    return {
        map: buildEntityMap(customerSegmentsData?.queryCustomerSegments),
        loading: customerSegmentsLoading,
    }
}
